import React, { useEffect, useState } from 'react';
import { SearchData, StoreIdentifierName } from 'types';
import { useAppSelector } from 'store/store';
import { searchDebouncedInputSelector } from 'store/selectors/saytSelector';
import { setSearchInput } from 'store/slices/sayt';
import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';
import getSearchUtils from '../SearchUtils';
import KeywordSuggestions from './KeywordSuggestions';
import ProductSuggestions from './ProductSuggestions';
import './SAYTSuggestionsV2.scss';

type SAYTSuggestionsV2Props = {
  storeIdentifierName: StoreIdentifierName;
  searchData: SearchData;
  submitSearch: (term: string, type: string, url?: string, isSeeAllResultsLink?: boolean) => string;
  searchTerm: string;
  thumbnailApi: string;
  mobileDrawer?: boolean;
};

const SAYTSuggestionsV2 = ({
  storeIdentifierName,
  searchData,
  submitSearch,
  searchTerm,
  thumbnailApi,
  mobileDrawer,
}: SAYTSuggestionsV2Props) => {
  const debouncedSearchInput = useAppSelector(searchDebouncedInputSelector);
  const [showProductSuggestions, setShowProductSuggestions] = useState(false);
  const {
    searchFromSuggestion,
    clickSuggestedTerm,
    evaluateViewAllUrl,
    getProductImage,
    getSeoURL,
  } = getSearchUtils(storeIdentifierName);

  useEffect(() => {
    if (debouncedSearchInput?.length >= 20 && searchData.products.length > 0) {
      setShowProductSuggestions(true);
    } else {
      setShowProductSuggestions(false);
    }
  }, [debouncedSearchInput, searchData.products]);

  useEffect(() => {
    if (mobileDrawer) {
      const handleContainerHeight = () => {
        const visualViewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const searchResultsContainer = document.querySelector(
          '.dsg-search-suggestion-container-v2',
        ) as HTMLElement;
        if (searchResultsContainer) {
          searchResultsContainer.style.maxHeight = `${visualViewportHeight - 64}px`;
          searchResultsContainer.style.overflow = 'auto';
        }
      };

      handleContainerHeight();
      window.visualViewport?.addEventListener('resize', handleContainerHeight);
      return () => {
        window.visualViewport?.removeEventListener('resize', handleContainerHeight);
      };
    }
    return undefined;
  }, [mobileDrawer]);

  return (
    <div
      className="dsg-search-suggestion-container-v2 hmf-py-xxs"
      data-testid="sayt-suggestion-container-v2">
      {searchData?.suggestions && searchTerm && searchTerm.length > 2 && (
        <div className="suggestion-container-v2">
          <ErrorBoundaryWithLogger componentId="SAYTSuggestionsV2-KeywordSuggestions">
            <KeywordSuggestions
              suggestions={searchData.suggestions}
              submitSearch={submitSearch}
              searchTerm={searchTerm}
              clickSuggestedTerm={clickSuggestedTerm}
              setSearchTerm={setSearchInput}
              searchFromSuggestion={searchFromSuggestion}
            />
          </ErrorBoundaryWithLogger>
          {showProductSuggestions && (
            <ErrorBoundaryWithLogger componentId="SAYTSuggestionsV2-ProductSuggestions">
              <ProductSuggestions
                productSuggestions={searchData.products}
                searchTerm={searchTerm}
                thumbnailApi={thumbnailApi}
                getProductImage={getProductImage}
                getSeoURL={getSeoURL}
                evaluateViewAllUrl={evaluateViewAllUrl}
              />
            </ErrorBoundaryWithLogger>
          )}
        </div>
      )}
      {mobileDrawer && searchTerm.length !== 0 && (
        <a
          data-testid="see-all-results"
          href={submitSearch(searchTerm, 'enterSearchTerm', '', true)}
          className="hmf-body-bold-l hmf-text-decoration-none label-primary-static see-all-button hmf-display-block hmf-px-s hmf-py-xs"
          aria-label="See all results">
          See all results for {searchTerm}
        </a>
      )}
    </div>
  );
};

export default SAYTSuggestionsV2;
